import { ExpiryTimeGranularityType, ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useRef, useState } from 'react';
import {
  Button, useTranslate, useRecordContext, Form,
} from 'react-admin';
import CloseIcon from '@mui/icons-material/Close';
import {
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Box,
  IconButton,
  Paper,
  PaperProps,
} from '@mui/material';
import {
  FormProvider, useForm, useFormContext, FieldValues,
} from 'react-hook-form';
import { TimeGranularityLabel } from '../../../../utils/timeGranularityUtils';
import { GrayAutocompleteArrayInputWithUpperLabel } from './GrayAutocompleteArrayInputWithUpperLabel';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from './GrayExpiryTimeGranularityInputWithUpperLabel';

type ReminderPeriodChoice = ExpiryTimeGranularity & { id: string };

const TIME_GRANULARITY_OPTIONS: ReminderPeriodChoice[] = [
  { id: 'day-1', type: ExpiryTimeGranularityType.Day, value: 1 },
  { id: 'week-1', type: ExpiryTimeGranularityType.Week, value: 1 },
  { id: 'month-1', type: ExpiryTimeGranularityType.Month, value: 1 },
];

const DEFAULT_REMINDER_PERIOD_IDS = new Set(
  TIME_GRANULARITY_OPTIONS.map((option) => option.id),
);

type CustomReminderPeriodInputProps = {
  helperText?: string;
  label?: string;
  isRequired?: boolean;
};

type ReminderPeriodCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  onAddSuggestion: (choice: ReminderPeriodChoice) => void;
};

const isDefaultReminderPeriod = (id: string) => DEFAULT_REMINDER_PERIOD_IDS.has(id);

const toReminderPeriodChoice = (granularity: ExpiryTimeGranularity): ReminderPeriodChoice => ({
  ...granularity,
  id: `${granularity.type}-${granularity.value}`,
});

const findReminderPeriodChoice = (
  id: string,
  choices: ReminderPeriodChoice[],
): ReminderPeriodChoice | undefined => choices.find((choice) => choice.id === id);

const toExpiryTimeGranularity = (
  choice: ReminderPeriodChoice,
): ExpiryTimeGranularity => ({
  type: choice.type,
  value: choice.value,
});

const ReminderPeriodOptionText = ({
  onRemove,
}: {
  onRemove: (choice: ReminderPeriodChoice) => void;
}) => {

  const record = useRecordContext<ReminderPeriodChoice>();
  const translate = useTranslate();
  if (!record?.type) return null;
  const isDefault = isDefaultReminderPeriod(record.id);
  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 1,
      }}
    >
      <span>{TimeGranularityLabel(record, translate)}</span>
      {!isDefault && (
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {

            event.stopPropagation();
            onRemove(record);

          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

};

const ReminderPeriodCreateDialog = ({
  open,
  onClose,
  onAddSuggestion,
}: ReminderPeriodCreateDialogProps) => {

  const translate = useTranslate();
  const inlineForm = useForm({
    defaultValues: {
      draft: { type: ExpiryTimeGranularityType.Month, value: 1 },
    },
  });

  const handleClose = () => {

    inlineForm.reset();
    onClose();

  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {translate('resources.certification-categories.text.custom_reminder_period_title')}
        <IconButton
          onClick={handleClose}
          aria-label={translate('general.text.close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{
        mt: '25px',
      }}>
        <FormProvider {...inlineForm}>
          <Form id="reminder-period-create-dialog-form"
            onSubmit={(data: FieldValues) => {

              const choice = toReminderPeriodChoice(data.draft);
              onAddSuggestion(choice);
              handleClose();

            }}>
            <GrayExpiryTimeGranularityInputWithUpperLabel
              source="draft"
              label={false}
            />
          </Form>
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button label="ra.action.cancel" onClick={handleClose} />
        <Button label="ra.action.add" type="submit" form="reminder-period-create-dialog-form" />
      </DialogActions>
    </Dialog>
  );

};

export const CustomReminderPeriodInput = ({
  helperText,
  label = 'resources.certification-categories.fields.reminder_periods',
  isRequired,
}: CustomReminderPeriodInputProps) => {

  const translate = useTranslate();
  const record = useRecordContext<{ reminderPeriods?: ExpiryTimeGranularity[] }>();

  const addToSuggestions = (
    prev: ReminderPeriodChoice[],
    choice: ReminderPeriodChoice,
  ) => (prev.some((c) => c.id === choice.id) ? prev : [...prev, choice]);

  const { getValues, setValue } = useFormContext();

  const [reminderPeriodSuggestions, setReminderPeriodSuggestions] = useState(() => {

    const fromRecord = (record?.reminderPeriods ?? []).map(toReminderPeriodChoice);
    return fromRecord.reduce(addToSuggestions, TIME_GRANULARITY_OPTIONS);

  });

  const [reminderPeriodCreateDialogOpen, setReminderPeriodCreateDialogOpen] = useState(false);

  const suggestionsRef = useRef(reminderPeriodSuggestions);
  suggestionsRef.current = reminderPeriodSuggestions;

  const handleAddSuggestion = (choice: ReminderPeriodChoice) => {

    const next = addToSuggestions(suggestionsRef.current, choice);
    suggestionsRef.current = next;
    setReminderPeriodSuggestions(next);

    const currentReminders = getValues('reminderPeriods') ?? [];
    if (!currentReminders.some((r: ExpiryTimeGranularity) => r.type === choice.type && r.value === choice.value)) {

      setValue('reminderPeriods', [...currentReminders, toExpiryTimeGranularity(choice)], { shouldDirty: true, shouldValidate: true });

    }

  };

  const formatGranularity = (granularity: ExpiryTimeGranularity) => {

    return TimeGranularityLabel(granularity, translate);

  };

  const removeFromSuggestions = (choice: ReminderPeriodChoice) => {

    setReminderPeriodSuggestions((prev) => prev.filter((item) => item.id !== choice.id));

  };

  const AddReminderPeriodPaper = (paperProps: PaperProps) => {

    const { children, ...rest } = paperProps;

    return (
      <Paper {...rest} elevation={0}>
        {children}
        <Button
          label="resources.certification-categories.text.custom_reminder_create"
          onMouseDown={(event) => {

            event.preventDefault();

          }}
          onClick={() => setReminderPeriodCreateDialogOpen(true)}
          fullWidth
          sx={{
            justifyContent: 'center',
            textAlign: 'center',
            bgcolor: '#E5F1EA',
            borderRadius: '3px',
            m: '4px',
            width: 'calc(100% - 8px)',
            '&:hover': { bgcolor: '#ACB7B1' },
          }}
        />
      </Paper>
    );

  };

  return (
    <>
      <GrayAutocompleteArrayInputWithUpperLabel
        source="reminderPeriods"
        forcePopupIcon
        label={label}
        helperText={helperText}
        isRequired={isRequired}
        TextFieldProps={{
          FormHelperTextProps: {
            sx: {
              margin: 0,
              marginLeft: 0,
            },
          },
        }}
        optionText={<ReminderPeriodOptionText onRemove={removeFromSuggestions} />}
        inputText={(choice) => formatGranularity(choice)}
        matchSuggestion={(filter, choice) => formatGranularity(choice).toLowerCase().includes(filter.toLowerCase())}
        format={(value?: ExpiryTimeGranularity[]) => (value ?? []).map((granularity) => toReminderPeriodChoice(granularity).id)}
        parse={(ids?: string[]) => (ids ?? [])
          .map((id) => findReminderPeriodChoice(id, suggestionsRef.current))
          .filter((choice): choice is ReminderPeriodChoice => choice !== undefined)
          .map(toExpiryTimeGranularity)
        }
        renderTags={(value, getTagProps) => (value as ReminderPeriodChoice[]).map((option: ReminderPeriodChoice, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={formatGranularity(option)}
            size="small"
          />
        ))
        }
        choices={reminderPeriodSuggestions}
        PaperComponent={AddReminderPeriodPaper}
        componentsProps={{
          popper: {
            sx: { zIndex: 1200 },
          },
        }}
      />
      <ReminderPeriodCreateDialog
        open={reminderPeriodCreateDialogOpen}
        onClose={() => setReminderPeriodCreateDialogOpen(false)}
        onAddSuggestion={handleAddSuggestion}
      />
    </>
  );

};
